import I18n from 'i18n-js';
import React from 'react';
import { debounce } from 'lodash';

import { IUserContext, withUserContext } from '../components/user_context_provider';
import FormGroup from '../components/form_group';
import OffersNudge from './offers_nudge';
import RecommendedOfferNudge from './recommended_offer_nudge';
import OffersCurrencyConversion from './offers_currency_conversion';
import OfferAmountInputs from './offer_amount_inputs';
import { CombinedSaleAndShippingNotice, TimelineNotice, SalesTaxNotice } from './offer_notices';
import { UserInputOffer } from './negotiation_types';
import {
  core_apimessages_Money as IMoney,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
  reverb_config_cache_ExchangeRate as ExchangeRate,
  CommonsOffersNewOfferForm,
  reverb_pricing_PriceRecommendation as PriceRecommendation,
} from '../rql';
import { RCAlertBox, RCCheckbox, RCTextArea } from '@reverbdotcom/cadence/components';
import { MoneyInput } from '../components/money_input';
import { parseAmount, parseAmountCents } from '../money';
import { FilterChip } from '../components/filter_chip';

const PERCENTAGE_BUTTON_CHOICES = [5, 10, 15];

export interface IExternalProps {
  listing: CommonsOffersNewOfferForm.Listing;
  buyerHasAtLeastOneAcceptedOffer: boolean;
  exchangeRates: ExchangeRate[];
  currency: string;
  onOfferChange: (any) => void;
  offer: UserInputOffer;
  party: NegotiationParty;
  autoFocus?: boolean;
  messageInput?: React.ReactNode;
  showTaxAtCheckoutNotices?: boolean;
  recommendedPriceDropPercent?: number;
  preventLowballOffers?: boolean;
  wouldBeAutoRejected?: boolean;
  modalDialogVersion?: boolean;
  useDesignChanges?: boolean;
  priceRecommendation?: PriceRecommendation;
}

export type IProps = IExternalProps & IUserContext;

function offerIsBelowLowballThreshold(lowballOfferPercentage, listingPrice, offerPrice) {
  if (!lowballOfferPercentage || !listingPrice?.amountCents || !offerPrice?.amountCents) { return false; }

  return offerPrice.amountCents < Math.round((lowballOfferPercentage / 100.0) * listingPrice.amountCents);
}

function EstimatedValueAlert({ offerPrice, recommendedLowPrice, recommendedHighPrice, belowLowballThreshold }) {
  if (!recommendedLowPrice?.amountCents || !recommendedHighPrice?.amountCents) { return null; }

  const outOfRange = offerPrice && (offerPrice.amountCents < recommendedLowPrice.amountCents);

  let alertType, alertMessage;
  if (belowLowballThreshold) {
    alertType = 'error';
    alertMessage = I18n.t('commons.offers.estimatedValueAlert.belowLowballThreshold');
  } else if (outOfRange) {
    alertType = 'warning';
    alertMessage = I18n.t('commons.offers.estimatedValueAlert.outOfRange');
  } else {
    alertType = 'info';
  }

  return (
    <RCAlertBox
      type={alertType}
    >
      <strong>{I18n.t('commons.offers.estimatedValueAlert.label')}</strong> {recommendedLowPrice.display} - {recommendedHighPrice.display}
      {alertMessage && (
        <p>
          {alertMessage}
        </p>
      )}
    </RCAlertBox>
  );
}

function OfferNotices({ listing, shop, party, showTaxAtCheckoutNotices }) {
  return (
    <div className="offers-form__notices">
      <CombinedSaleAndShippingNotice listing={listing} />
      {party === NegotiationParty.BUYER && (
        <TimelineNotice shop={shop} />
      )}
      {showTaxAtCheckoutNotices && (
        <SalesTaxNotice shop={shop} />
      )}
    </div>
  );
}

function OfferPercentageButton({ percent, listingPrice, offerPrice, dispatch }) {
  const percentageFloat = (100 - percent) / 100.0;
  const discountedPriceCents = Math.round(percentageFloat * listingPrice.amountCents);
  const discountedPrice = parseAmountCents(discountedPriceCents, listingPrice.currency);

  return (
    <span className="offers-form__discount-percentage-button">
      <FilterChip
        selected={offerPrice.amountCents == discountedPriceCents}
        onClick={() => {
          dispatch({ type: 'setPrice', value: discountedPrice.amount });
        }}
      >
        {I18n.t('commons.offers.percentOff', { percent: percent })}
      </FilterChip>
    </span>
  );
}

export function OfferCreateFormFields(props: IProps) {
  const [agreementChecked, setAgreementChecked] = React.useState(props.buyerHasAtLeastOneAcceptedOffer);
  const [debouncedOfferPrice, setDebouncedOfferPrice] = React.useState('');

  const { shop, price: listingPrice } = props.listing;
  const { price: offerPrice } = props.offer;

  const isBuyer = props.party === NegotiationParty.BUYER;
  const shouldUseInteractiveNudge = !props.buyerHasAtLeastOneAcceptedOffer || props.recommendedPriceDropPercent;
  const parsedOfferPrice = offerPrice && parseAmount(offerPrice, props.currency);
  const parsedDebouncedOfferPrice = debouncedOfferPrice && parseAmount(debouncedOfferPrice, props.currency);
  const belowLowballThreshold = props.preventLowballOffers && offerIsBelowLowballThreshold(props.listing.shop.lowballOfferPercentage, listingPrice, parsedDebouncedOfferPrice);

  // Price recommendation-based alerts are triggered by the offer amount input, and we don't want alerts to
  // immediately fire if the user is in the middle of typing a number, so we delay setting "debouncedOfferPrice"
  // by 200ms and attach the alert rendering to that piece of state rather than to the input state itself
  const debounceOfferPrice = React.useCallback(debounce((newOfferPrice) => {
    setDebouncedOfferPrice(newOfferPrice);
  }, 200), []);

  React.useEffect(() => {
    debounceOfferPrice(offerPrice);
  }, [offerPrice, debounceOfferPrice]);

  function messageLabel() {
    if (props.party === NegotiationParty.SELLER) {
      return I18n.t('commons.offers.sellerMessageLabel');
    } else if (props.useDesignChanges) {
      return I18n.t('commons.offers.buyerShareAMessageLabel');
    }

    return I18n.t('commons.offers.buyerMessageLabel');
  }

  function shippingPriceInShopCurrency(): IMoney | undefined {
    const prices = props.listing.shipping.shippingPrices;

    if (prices.length > 0) {
      return prices[0].originalRate;
    }
  }

  return (
    <div className="offers-form__fields">
      <OfferAmountInputs
        offer={props.offer}
        listing={props.listing}
        party={props.party}
        action={NegotiationAction.CREATE}
        currency={props.currency}
        taxable={props.listing}
        onOfferChange={props.onOfferChange}
        autoFocus={props.autoFocus}
        modalDialogVersion={props.modalDialogVersion}
        useDesignChanges={props.useDesignChanges}
      />

      {!props.useDesignChanges && isBuyer && (
        <>
          {shouldUseInteractiveNudge && (
            <RecommendedOfferNudge
              offer={props.offer}
              onOfferChange={props.onOfferChange}
              originalPrice={listingPrice.amount}
              recommendedPriceDropPercent={props.recommendedPriceDropPercent}
              currency={props.currency}
              listingCondition={props.listing.condition}
              preventLowballOffers={props.preventLowballOffers}
              wouldBeAutoRejected={props.wouldBeAutoRejected}
              modalDialogVersion={props.modalDialogVersion}
            />
          )}

          {!shouldUseInteractiveNudge && (
            <OffersNudge
              offerAmount={offerPrice}
              originalAmount={listingPrice.amount}
              currency={props.currency}
              preventLowballOffers={props.preventLowballOffers}
              wouldBeAutoRejected={props.wouldBeAutoRejected}
            />
          )}
        </>
      )}

      {props.useDesignChanges && isBuyer && (
        <div className="mb-3">
          {props.priceRecommendation && (
            <EstimatedValueAlert
              offerPrice={parsedDebouncedOfferPrice}
              recommendedLowPrice={props.priceRecommendation.priceLow}
              recommendedHighPrice={props.priceRecommendation.priceHigh}
              belowLowballThreshold={belowLowballThreshold}
            />
          )}

          {!props.priceRecommendation && (
            <>
              {!belowLowballThreshold && (
                <>
                  {PERCENTAGE_BUTTON_CHOICES.map((percent) => {
                    return (
                      <OfferPercentageButton
                        key={percent}
                        percent={percent}
                        listingPrice={listingPrice}
                        offerPrice={parsedOfferPrice}
                        dispatch={props.onOfferChange}
                      />
                    );
                  })}
                </>
              )}

              {belowLowballThreshold && (
                <RCAlertBox type="error">
                  {I18n.t('commons.offers.estimatedValueAlert.belowLowballThreshold')}
                </RCAlertBox>
              )}
            </>
          )}

          {parsedOfferPrice && props.currency != shop.currency && (
            <section className="offers-form__currency-difference-alert">
              <p>
                {I18n.t('commons.offers.currencyMayVary')}
              </p>
            </section>
          )}
        </div>
      )}

      {props.party === NegotiationParty.SELLER && (
        <MoneyInput
          label={I18n.t('commons.offers.label.shipping')}
          onChange={(evt) => {
            if (props.modalDialogVersion) {
              props.onOfferChange({ type: 'setShippingPrice', value: evt.target.value });
            } else {
              props.onOfferChange({ shippingPrice: evt.target.value });
            }
          }}
          id="offer-shipping-price"
          name="offer-shipping-price"
          currencyCode={props.currency}
          value={props.offer.shippingPrice}
          required
        />
      )}

      {!props.useDesignChanges && (
        <OffersCurrencyConversion
          offerAmount={offerPrice}
          shippingPrice={shippingPriceInShopCurrency()}
          rates={props.exchangeRates}
          userCurrency={props.currency}
          shopCurrency={shop.currency}
        />
      )}

      {props.messageInput && (
        <FormGroup label={messageLabel()} tagOptional>
          {props.messageInput}
        </FormGroup>
      )}

      {!props.messageInput && (
        <RCTextArea
          label={messageLabel()}
          onChange={(evt) => {
            if (props.modalDialogVersion) {
              props.onOfferChange({ type: 'setMessage', value: evt.target.value });
            } else {
              props.onOfferChange({ message: evt.target.value });
            }
          }}
          name="message"
          id="offer-message"
          value={props.offer.message}
          helpText={props.useDesignChanges && I18n.t('commons.offers.buyerShareAMessageHelpText')}
          tag={props.useDesignChanges ? 'optional' : undefined}
        />
      )}

      {!props.useDesignChanges && (
        <OfferNotices
          listing={props.listing}
          shop={shop || { name: '' }}
          party={props.party}
          showTaxAtCheckoutNotices={props.showTaxAtCheckoutNotices}
        />
      )}

      {!props.useDesignChanges && isBuyer && (
        <div className="offers-form__terms">
          <RCCheckbox
            id="offers-form-terms-checkbox"
            checked={agreementChecked}
            label={I18n.t('commons.offers.terms')}
            name="offers-form-terms-checkbox"
            required
            onChange={() => { setAgreementChecked(!agreementChecked); }}
          />
        </div>
      )}
    </div>
  );
}

export default withUserContext(OfferCreateFormFields);
